<script setup lang="ts">
import { breakpointsTailwind } from '@vueuse/core'
import type { Component } from 'vue'
import Drawer from './components/Drawer/index.vue'
import DrawerElementalLists from './pages/DrawerElementalLists/index.vue'
import type { SideBarMode } from '~~/src/stores/sidebar'
import { useSidebarStore } from '~~/src/stores/sidebar'
import { HEADER_HEIGHT } from '~~/src/composables/layout'

defineOptions({
  name: 'DrawerSidebarRight',
})

const { state } = useSidebarStore()
const { isSmaller } = useBreakpoints(breakpointsTailwind)
const isMobile = ref(isSmaller('md'))
useResizeObserver(window.document.body, () => {
  isMobile.value = isSmaller('md')
})

const hiddeElementalLists = ref(false)

watch(
  () => state.mode,
  (mode) => {
    setTimeout(() => {
      hiddeElementalLists.value = mode !== 'view'
    }, 300)
  },
  { immediate: true },
)

const views: Record<SideBarMode, Component> = {
  'view': DrawerElementalLists,
  'edit': defineAsyncComponent(() => import('./pages/EditElemental/index.vue')),
  'folder': defineAsyncComponent(() => import('./pages/FolderView/index.vue')),
  'export': defineAsyncComponent(() => import('./pages/Export/index.vue')),
  'prompt-injector': defineAsyncComponent(() => import('./pages/PromptInjector/index.vue')),
  'webhooks': defineAsyncComponent(() => import('./pages/Webhooks/index.vue')),
  'webhooks-create': defineAsyncComponent(() => import('./pages/Webhooks/Create.vue')),
  'workflows': defineAsyncComponent(() => import('./pages/Workflows/index.vue')),
  'workflows-create': defineAsyncComponent(() => import('./pages/Workflows/Create.vue')),
  'workflows-preview': defineAsyncComponent(() => import('./pages/Workflows/Preview.vue')),
  'workflows-request-details': defineAsyncComponent(() => import('./pages/Workflows/Request.vue')),
}

const isModeState = Object.keys(views).includes(state.mode)
</script>

<template>
  <div v-if="!isMobile" relative>
    <Drawer id="drawer-sidebar-right isolate relative overflow-hidden">
      <ClientOnly>
        <div class="absolute right-0 top-0 bottom-0 z-10 max-w-full bg-white  h-full w-full no-scrollbar">
          <DrawerElementalLists
            class="transition-opacity transition-transform ease-in-out duration-350 no-scrollbar"
            :class="[
              state.mode === 'view' ? 'opacity-100' : 'opacity-0 translate-y-[10px] overflow-y-hidden duration-300 pointer-events-none',
            ]"
          />
        </div>
        <TransitionSoftSlide direction="down">
          <div v-if="isModeState" class="absolute right-0 top-0 bottom-0 z-20 max-w-full bg-white h-full w-full overflow-hidden">
            <component :is="views[state.mode]" class="no-scrollbar" />
          </div>
        </TransitionSoftSlide>
      </ClientOnly>
    </Drawer>
  </div>
  <Teleport to="body">
    <div
      v-if="isMobile" class="absolute right-0 top-0 -z-999 max-w-full"
      :class="{
        'z-999!': state.isOpen,
        'invisible': !state.isOpen,
      }"
      :style="{
        paddingTop: `${HEADER_HEIGHT}px`,
        height: 'calc(100% - 57px)',
      }"
    >
      <Drawer id="drawer-sidebar-right isolate">
        <ClientOnly>
          <template v-if="isModeState">
            <TransitionSoftSlide direction="down">
              <Suspense>
                <component :is="views[state.mode]" />
              </Suspense>
            </TransitionSoftSlide>
          </template>
          <template v-else>
            <Suspense>
              <component :is="views[state.mode]" />
            </Suspense>
          </template>
        </ClientOnly>
      </Drawer>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.no-scrollbar {
  scrollbar-width: thin;
  scrollbar-gutter: stable;
  overflow-y: auto;
 }
</style>
